'use client';

/**
 * Floating WhatsApp Button Component
 * Beautiful floating action button that opens WhatsApp on mobile/desktop
 */
import React from 'react';

import Icon from './Icon';

interface WhatsAppButtonProps {
  phoneNumber: string; // E.g. '+1234567890' or 'https://wa.me/1234567890?text=Hello'
  message?: string;
  size?: 'sm' | 'md' | 'lg';
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  className?: string;
}

const WhatsAppButton: React.FC<WhatsAppButtonProps> = ({ phoneNumber, message = 'Hola, me gustaría obtener más información', size = 'md', position = 'bottom-right', className = '' }) => {
  // Generate WhatsApp URL based on platform and phoneNumber format
  const generateWhatsAppUrl = () => {
    // Ensure phoneNumber is a string regardless of what was passed at runtime
    const phoneOrLink = String(phoneNumber ?? '');

    // If phoneNumber is already a full URL, use it directly
    if (phoneOrLink.startsWith('http://') || phoneOrLink.startsWith('https://')) {
      return phoneOrLink;
    }

    // Otherwise, generate URL from phone number
    const encodedMessage = encodeURIComponent(message || 'Hola, me gustaría obtener más información');
    const cleanPhone = phoneOrLink.replace(/\D/g, ''); // Remove non-digits

    // Check if mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    if (isMobile) {
      // Use WhatsApp app URL scheme for mobile
      return `whatsapp://send?phone=${cleanPhone}&text=${encodedMessage}`;
    } else {
      // Use web WhatsApp for desktop
      return `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
    }
  };

  // Size classes
  const sizeClasses = {
    sm: 'w-12 h-12',
    md: 'w-14 h-14',
    lg: 'w-16 h-16',
  };

  // Icon size based on button size
  const iconSizes = {
    sm: 20,
    md: 24,
    lg: 28,
  };

  const handleClick = () => {
    const url = generateWhatsAppUrl();
    window.open(url, '_blank');
  };

  return (
    <button
      onClick={handleClick}
      data-testid="whatsapp-button"
      className={`
                fixed z-9999
                ${sizeClasses[size]}
                ${className}
                bg-green-500 hover:bg-green-600
                text-white
                rounded-full
                shadow-lg hover:shadow-xl
                transition-all duration-600
                flex items-center justify-center
                group
                animate-pulse hover:animate-none
                border-2 border-transparent
                cursor-pointer
            `}
      style={{
        bottom: '20px',
        right: '20px',
        minWidth: size === 'sm' ? '48px' : size === 'md' ? '56px' : '64px',
        minHeight: size === 'sm' ? '48px' : size === 'md' ? '56px' : '64px',
      }}
    >
      {/* Pulse animation background */}
      <div
        className="
                absolute inset-0
                bg-green-500
                rounded-full
                animate-ping
                opacity-20
                group-hover:animate-none
            "
      />

      {/* Main button content */}
      <div className="relative z-10 flex items-center justify-center">
        <span className="text-white text-2xl">
          <Icon name="whatsapp" style="brands" size={iconSizes[size]} className="text-white" />
        </span>
      </div>

      {/* Tooltip */}
      <div
        className="
                absolute bottom-full right-0 mb-2
                bg-gray-800 text-white text-xs
                px-2 py-1 rounded
                opacity-0 group-hover:opacity-100
                transition-opacity duration-200
                whitespace-nowrap
                pointer-events-none
            "
      >
        ¡Contáctanos por WhatsApp!
        <div
          className="
                    absolute top-full right-3
                    w-0 h-0
                    border-l-4 border-r-4 border-t-4
                    border-l-transparent border-r-transparent border-t-gray-800
                "
        />
      </div>
    </button>
  );
};

export default WhatsAppButton;
